interface ProblemSolutionHighlight {
  metric: string;
  label: string;
  description: string;
}

interface ProblemSolutionData {
  problem: {
    title: string;
    description: string;
    challenges: string[];
    result?: {
      title?: string;
      subtitle?: string;
      text?: string;
      highlights?: ProblemSolutionHighlight[];
    };
  };
  solution: {
    title: string;
    description: string;
    benefits: string[];
  };
}

/**
 * ProblemSolutionBlock
 *
 * Side-by-side "problem vs. solution" panel used by `problemSolution`
 * sections in migration / implementation JSONs. Renders the pain framing
 * (with optional metric highlights) on the left and the proposed solution
 * with bullet benefits on the right.
 */
export default function ProblemSolutionBlock({
  problem,
  solution,
}: ProblemSolutionData) {
  if (!Array.isArray(problem?.challenges) || problem.challenges.length === 0) {
    throw new Error(
      "ProblemSolutionBlock: `problem.challenges` array is empty or invalid."
    );
  }
  if (!Array.isArray(solution?.benefits) || solution.benefits.length === 0) {
    throw new Error(
      "ProblemSolutionBlock: `solution.benefits` array is empty or invalid."
    );
  }

  return (
    <div className="problem-solution">
      <div className="problem-solution-grid">
        <article className="ps-card ps-card-problem">
          <h3>{problem.title}</h3>
          <p>{problem.description}</p>
          <ul>
            {problem.challenges.map((challenge) => (
              <li key={challenge}>{challenge}</li>
            ))}
          </ul>
        </article>
        <article className="ps-card ps-card-solution">
          <h3>{solution.title}</h3>
          <p>{solution.description}</p>
          <ul>
            {solution.benefits.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
        </article>
      </div>
      {problem.result?.highlights &&
      problem.result.highlights.length > 0 ? (
        <div className="ps-highlights">
          {problem.result.title ? <h5>{problem.result.title}</h5> : null}
          {problem.result.subtitle ? <p>{problem.result.subtitle}</p> : null}
          {problem.result.text ? <p>{problem.result.text}</p> : null}
          <div className="ps-highlights-grid">
            {problem.result.highlights.map((hl) => (
              <div className="ps-highlight" key={`${hl.metric}-${hl.label}`}>
                <strong>{hl.metric}</strong>
                <span className="ps-highlight-label">{hl.label}</span>
                <p>{hl.description}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
